// ============================================
// Tienda Focus — Animations & Visual Effects
// ============================================

// ─── Solar Particle System (Canvas) ────────
class SolarParticles {
  constructor(canvasId) {
    this.canvas = document.getElementById(canvasId);
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');
    this.particles = [];
    this.mouse = { x: null, y: null };
    this.resize();
    this.init();
    this.animate();

    window.addEventListener('resize', () => this.resize());
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouse.x = e.clientX - rect.left;
      this.mouse.y = e.clientY - rect.top;
    });
    this.canvas.addEventListener('mouseleave', () => {
      this.mouse.x = null;
      this.mouse.y = null;
    });
  }

  resize() {
    const parent = this.canvas.parentElement;
    this.canvas.width = parent.offsetWidth;
    this.canvas.height = parent.offsetHeight;
  }

  init() {
    const count = Math.min(80, Math.floor(this.canvas.width / 15));
    this.particles = [];
    for (let i = 0; i < count; i++) {
      this.particles.push(this.createParticle());
    }
  }

  createParticle() {
    const colors = [
      'rgba(79, 70, 229, ',    // Indigo Real (Tecnológico y elegante)
      'rgba(13, 148, 136, ',   // Teal Limpio (Energía moderna y limpia)
      'rgba(2, 132, 199, ',    // Azul Eléctrico (Sleek tech y luces LED)
    ];
    return {
      x: Math.random() * this.canvas.width,
      y: Math.random() * this.canvas.height,
      size: Math.random() * 3 + 1,
      speedX: (Math.random() - 0.5) * 0.8,
      speedY: (Math.random() - 0.5) * 0.8,
      color: colors[Math.floor(Math.random() * colors.length)],
      opacity: Math.random() * 0.5 + 0.2,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.02 + 0.01
    };
  }

  animate() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    this.particles.forEach((p, i) => {
      p.pulse += p.pulseSpeed;
      const opacity = p.opacity + Math.sin(p.pulse) * 0.15;
      const size = p.size + Math.sin(p.pulse) * 0.5;

      // Draw glow
      const gradient = this.ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size * 4);
      gradient.addColorStop(0, p.color + (opacity * 0.6) + ')');
      gradient.addColorStop(1, p.color + '0)');
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, size * 4, 0, Math.PI * 2);
      this.ctx.fillStyle = gradient;
      this.ctx.fill();

      // Draw core
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      this.ctx.fillStyle = p.color + opacity + ')';
      this.ctx.fill();

      // Mouse interaction
      if (this.mouse.x !== null) {
        const dx = this.mouse.x - p.x;
        const dy = this.mouse.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 120) {
          p.x -= dx * 0.02;
          p.y -= dy * 0.02;
        }
      }

      // Connect nearby particles
      for (let j = i + 1; j < this.particles.length; j++) {
        const p2 = this.particles[j];
        const dx = p.x - p2.x;
        const dy = p.y - p2.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 95) {
          this.ctx.beginPath();
          this.ctx.strokeStyle = p.color + (0.3 * (1 - dist / 95)) + ')';
          this.ctx.lineWidth = 0.6;
          this.ctx.moveTo(p.x, p.y);
          this.ctx.lineTo(p2.x, p2.y);
          this.ctx.stroke();
        }
      }

      // Move
      p.x += p.speedX;
      p.y += p.speedY;

      // Wrap
      if (p.x < -10) p.x = this.canvas.width + 10;
      if (p.x > this.canvas.width + 10) p.x = -10;
      if (p.y < -10) p.y = this.canvas.height + 10;
      if (p.y > this.canvas.height + 10) p.y = -10;
    });

    requestAnimationFrame(() => this.animate());
  }
}

// ─── Scroll Reveal Animations ──────────────
class ScrollReveal {
  constructor() {
    this.elements = document.querySelectorAll('[data-reveal]');
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.revealDelay || 0;
            setTimeout(() => {
              entry.target.classList.add('revealed');
            }, delay);
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    this.elements.forEach(el => this.observer.observe(el));
  }
}

// ─── Counter Animation ─────────────────────
class CounterAnimation {
  constructor() {
    this.counters = document.querySelectorAll('[data-count]');
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateCounter(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    this.counters.forEach(el => this.observer.observe(el));
  }

  animateCounter(el) {
    const target = parseInt(el.dataset.count);
    const duration = 2000;
    const startTime = performance.now();

    const update = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      const current = Math.floor(eased * target);
      
      el.textContent = current.toLocaleString('es-CL');
      
      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        el.textContent = target.toLocaleString('es-CL') + (el.dataset.countSuffix || '');
      }
    };

    requestAnimationFrame(update);
  }
}

// ─── 3D Tilt Effect on Cards ───────────────
class TiltEffect {
  constructor(selector) {
    this.cards = document.querySelectorAll(selector);
    this.cards.forEach(card => {
      card.addEventListener('mousemove', (e) => this.handleMove(e, card));
      card.addEventListener('mouseleave', (e) => this.handleLeave(e, card));
    });
  }

  handleMove(e, card) {
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -5;
    const rotateY = ((x - centerX) / centerX) * 5;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  }

  handleLeave(e, card) {
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  }
}

// ─── Parallax Effect ───────────────────────
class Parallax {
  constructor() {
    this.elements = document.querySelectorAll('[data-parallax]');
    if (this.elements.length === 0) return;
    window.addEventListener('scroll', () => this.update(), { passive: true });
  }

  update() {
    const scrollY = window.scrollY;
    this.elements.forEach(el => {
      const speed = parseFloat(el.dataset.parallax) || 0.5;
      const rect = el.getBoundingClientRect();
      const offset = (scrollY - el.offsetTop) * speed;
      el.style.transform = `translateY(${offset}px)`;
    });
  }
}

// ─── Countdown Timer ───────────────────────
class Countdown {
  constructor(elementId, hours = 23) {
    this.element = document.getElementById(elementId);
    if (!this.element) return;

    // Set end time from localStorage or create new
    let endTime = localStorage.getItem('tienda_focus_countdown');
    if (!endTime || new Date(endTime) < new Date()) {
      const end = new Date();
      end.setHours(end.getHours() + hours);
      endTime = end.toISOString();
      localStorage.setItem('tienda_focus_countdown', endTime);
    }
    this.endTime = new Date(endTime);
    this.update();
    setInterval(() => this.update(), 1000);
  }

  update() {
    const now = new Date();
    const diff = this.endTime - now;

    if (diff <= 0) {
      // Reset countdown
      const end = new Date();
      end.setHours(end.getHours() + 24);
      this.endTime = end;
      localStorage.setItem('tienda_focus_countdown', end.toISOString());
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    this.element.innerHTML = `
      <div class="countdown-unit">
        <span class="countdown-number">${String(hours).padStart(2, '0')}</span>
        <span class="countdown-label">Horas</span>
      </div>
      <div class="countdown-separator">:</div>
      <div class="countdown-unit">
        <span class="countdown-number">${String(minutes).padStart(2, '0')}</span>
        <span class="countdown-label">Min</span>
      </div>
      <div class="countdown-separator">:</div>
      <div class="countdown-unit">
        <span class="countdown-number">${String(seconds).padStart(2, '0')}</span>
        <span class="countdown-label">Seg</span>
      </div>
    `;
  }
}

// ─── Smooth Scroll ─────────────────────────
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        const headerOffset = 80;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    });
  });
}

// ─── Typed Text Effect ─────────────────────
class TypedText {
  constructor(elementId, texts, speed = 80) {
    this.element = document.getElementById(elementId);
    if (!this.element) return;
    this.texts = texts;
    this.speed = speed;
    this.textIndex = 0;
    this.charIndex = 0;
    this.isDeleting = false;
    this.type();
  }

  type() {
    const currentText = this.texts[this.textIndex];
    
    if (this.isDeleting) {
      this.element.textContent = currentText.substring(0, this.charIndex - 1);
      this.charIndex--;
    } else {
      this.element.textContent = currentText.substring(0, this.charIndex + 1);
      this.charIndex++;
    }

    let delay = this.isDeleting ? this.speed / 2 : this.speed;

    if (!this.isDeleting && this.charIndex === currentText.length) {
      delay = 2000;
      this.isDeleting = true;
    } else if (this.isDeleting && this.charIndex === 0) {
      this.isDeleting = false;
      this.textIndex = (this.textIndex + 1) % this.texts.length;
      delay = 500;
    }

    setTimeout(() => this.type(), delay);
  }
}

// ─── Initialize All Animations ─────────────
function initAnimations() {
  // Particles
  new SolarParticles('hero-particles');

  // Scroll reveals
  new ScrollReveal();

  // Counters
  new CounterAnimation();

  // 3D tilt on category cards
  new TiltEffect('.category-card');

  // Parallax
  new Parallax();

  // Countdown
  new Countdown('promo-countdown', 23);

  // Smooth scroll
  initSmoothScroll();

  // Typed text
  new TypedText('typed-text', [
    'Energía Solar',
    'Accesorios para Autos',
    'Tecnología LED',
    'Productos Premium'
  ]);
}
